import {Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import {UsersService} from 'src/app/modules/shared/services/users.service';
import {Constants} from 'src/app/modules/shared/config/constants.config';
import {ThemeService} from '../../../shared/services/theme.service';
import {AppLoadingEventsEmitter} from '../../../shared/events/loading.events';
import {ActivatedRoute, Router} from '@angular/router';
import {MasterService} from '../../../shared/services/master.service';
import {AuthService} from '../../../shared/services/auth.service';
import * as moment from 'moment/moment';
import {DateService} from '../../../shared/services/date.service';
import {
    MasterAutoDiaryGenerationConfigViewModel,
    MasterAutoDiaryMealViewModel,
    MasterAutoDiaryRecipeViewModel
} from '../../../shared/viewModels/master.viewModel';

@Component({
    selector: 'app-master-auto-recipes-edit',
    templateUrl: './auto-recipes-edit.component.html',
    styleUrls: ['./auto-recipes-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoRecipesEditComponent implements OnInit {

    dateFrom: Date = new Date();
    dateTo: Date = new Date();
    meals?: MasterAutoDiaryMealViewModel[];

    originalRecipesByMeal = {};
    recipesByMeal = {};

    originalSelectedRecipesByMeal = {};
    selectedRecipesByMeal = {};

    showPreview = false;
    editConfig: MasterAutoDiaryGenerationConfigViewModel;
    is_active = false;
    allowSave = false;

    editId: number;

    constructor(
        private readonly usersService: UsersService,
        private readonly masterService: MasterService,
        private readonly dateService: DateService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        public readonly themeService: ThemeService,
        public readonly constants: Constants,
        public readonly router: Router,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly appIsLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly authService: AuthService,
        private route: ActivatedRoute,
    ) {
        this.dateTo = moment().add(1, 'days').toDate();
        this.changeDetectorRef.markForCheck();
    }

    ngOnInit() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);

        this.route.params.subscribe(params => {
            this.editId = params['id'];

            this.masterService
                .getMasterAutoDiaryRecipes(this.usersService.userProfile.id)
                .subscribe(dto => {
                    this.meals = dto.meals;

                    for (const meal of this.meals) {
                        const recipes = [];

                        for (const recipe of dto.recipes) {
                            for (const suitableMealId of recipe.suitableMealIds) {
                                // tslint:disable-next-line:triple-equals
                                if (suitableMealId == meal.id) {
                                    recipes.push(recipe);
                                }
                            }
                        }

                        this.recipesByMeal[meal.id] = recipes;
                        this.originalRecipesByMeal[meal.id] = recipes;
                    }

                    this.masterService
                        .getMasterAutoDiaryGenerationConfig(params['id'])
                        .subscribe(editConfigDto => {
                            this.editConfig = editConfigDto;

                            this.is_active = this.editConfig.is_active;
                            this.dateFrom = moment(this.editConfig.dateFrom).toDate();
                            this.dateTo = moment(this.editConfig.dateTo).toDate();

                            if (this.editConfig.is_active) {
                                this.showPreview = true;
                            }

                            for (const mealId of Object.keys(this.editConfig.recipes_per_meal)) {
                                const mealRecipes = this.editConfig.recipes_per_meal[mealId];
                                const recipesPerMeal = this.recipesByMeal[mealId];

                                for (const recipe of recipesPerMeal) {
                                    for (const selectedRecipeId of mealRecipes) {
                                        if (recipe.id === selectedRecipeId) {
                                            // tslint:disable-next-line:radix
                                            this.selectRecipe(parseInt(mealId), recipe.id, recipe);
                                        }
                                    }
                                }
                            }

                            this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                            this.changeDetectorRef.markForCheck();
                        });
                });
        });
    }

    onDateFromPickerChanged(date: Date) {
        this.dateFrom = date;
        this.changeDetectorRef.markForCheck();
    }

    onDateToPickerChanged(date: Date) {
        this.dateTo = date;
        this.changeDetectorRef.markForCheck();
    }

    onRecipeSearch($event: KeyboardEvent, mealId: number) {
        const recipes = [];
        const searchText = (<HTMLInputElement>$event.target).value.toLowerCase();

        if (searchText.length <= 0) {
            this.recipesByMeal[mealId] = this.originalRecipesByMeal[mealId];
            this.changeDetectorRef.markForCheck();
            return;
        }

        for (const recipe of this.originalRecipesByMeal[mealId]) {
            if (recipe.title.toLowerCase().includes(searchText)) {
                recipes.push(recipe);
            }
        }

        this.recipesByMeal[mealId] = recipes;
        this.changeDetectorRef.markForCheck();
    }

    onSelectedRecipeSearch($event: KeyboardEvent, mealId: number) {
        const recipes = [];
        const searchText = (<HTMLInputElement>$event.target).value.toLowerCase();

        if (searchText.length <= 0) {
            this.selectedRecipesByMeal[mealId] = this.originalSelectedRecipesByMeal[mealId];
            this.changeDetectorRef.markForCheck();
            return;
        }

        for (const recipe of this.originalSelectedRecipesByMeal[mealId]) {
            if (recipe.title.toLowerCase().includes(searchText)) {
                recipes.push(recipe);
            }
        }

        this.selectedRecipesByMeal[mealId] = recipes;
        this.changeDetectorRef.markForCheck();
    }

    selectRecipe(mealId: number, recipeId: number, recipe: any) {
        this.originalSelectedRecipesByMeal[mealId] = this.originalSelectedRecipesByMeal[mealId] || [];
        this.originalSelectedRecipesByMeal[mealId].push(recipe);

        this.selectedRecipesByMeal[mealId] = this.originalSelectedRecipesByMeal[mealId];

        this.recipesByMeal[mealId] = this.recipesByMeal[mealId].filter(x => x.id !== recipeId);
        this.originalRecipesByMeal[mealId] = this.originalRecipesByMeal[mealId].filter(x => x.id !== recipeId);

        this.changeDetectorRef.markForCheck();
    }

    unSelectRecipe(mealId: number, recipeId: number, recipe: any) {
        this.recipesByMeal[mealId] = this.recipesByMeal[mealId] || [];
        this.recipesByMeal[mealId].push(recipe);
        this.originalRecipesByMeal[mealId].push(recipe);

        this.selectedRecipesByMeal[mealId] = this.selectedRecipesByMeal[mealId].filter(x => x.id !== recipeId);
        this.originalSelectedRecipesByMeal[mealId] = this.originalSelectedRecipesByMeal[mealId].filter(x => x.id !== recipeId);

        this.changeDetectorRef.markForCheck();
    }

    sortRecipes(recipes: MasterAutoDiaryRecipeViewModel[]) {
        if (!recipes) {
            return [];
        }

        return recipes.sort((a, b) => a.title.localeCompare(b.title));
    }

    onIsActiveChange(is_active: boolean) {
        this.is_active = is_active;
        this.changeDetectorRef.markForCheck();
    }

    formatDate(date) {
        return moment(date).format('DD.MM.YYYY');
    }


    save() {
        const fromDate = moment(this.dateFrom);
        const toDate = moment(this.dateTo);

        if (fromDate > toDate) {
            alert('Наачалната дата не може да е по-голяма от крайната!');
            return;
        }

        const recipes = [];

        for (const meal of this.meals) {
            if (this.originalSelectedRecipesByMeal[meal.id]) {
                for (const recipe of this.originalSelectedRecipesByMeal[meal.id]) {
                    recipes.push(recipe);
                }
            }
        }

        if (recipes.length <= 0) {
            alert('Трябва да изберете поне 1 рецепта. Най-оптимално е когато изберете по 1 рецепта за всяко хранене.');
            return;
        }

        this.showPreview = true;
        this.allowSave = true;
        this.changeDetectorRef.markForCheck();
    }


    updateConfig() {
        if (!confirm('Сигурни ли сте, че искате да обновите тази конфигурация?')) {
            return;
        }

        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);

        const recipes = [];

        for (const meal of this.meals) {
            if (this.originalSelectedRecipesByMeal[meal.id]) {
                for (const recipe of this.originalSelectedRecipesByMeal[meal.id]) {
                    recipes.push({
                        'recipe_id': recipe.id,
                        'meal_id': meal.id
                    });
                }
            }
        }

        this.masterService
            .updateMasterAutoDiaryRecipes(
                {
                    id: this.editId,
                    dateFrom: moment(this.dateFrom).format('YYYY-MM-DD'),
                    dateTo: moment(this.dateTo).format('YYYY-MM-DD'),
                    recipes: recipes,
                    isActive: this.is_active
                }
            )
            .subscribe(() => {
                this.router.navigate(['/master/auto/recipes']);
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.changeDetectorRef.markForCheck();
            });
    }

    confirmEditConfig() {
        this.showPreview = false;
        this.changeDetectorRef.markForCheck();
    }
}
